// Message search (GET /me/messages/search): the box over the inbox (every
// chat) and the one a thread opens (that chat only). A result opens its thread
// at the message (`/chats/:id?seq=`, which ThreadView loads back to).
import { useInfiniteQuery } from "@tanstack/react-query";
import type { Schemas } from "@10twenty/api";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { api, errorMessage, unwrap } from "../api";
import {
  conversationTitle,
  inboxTime,
  isExpired,
  personName,
  withUsers,
  type Conversation,
  type Users,
} from "../chat/chat";
import { SEARCH_DEBOUNCE_MS, SEARCH_MAX, searchText, searchWords, snippet } from "../chat/search";
import { MemberAvatar } from "./MemberAvatar";

type SearchPage = Schemas["MessageSearchPage"];

/** `value`, once it has stayed the same for `ms`. */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/**
 * A search box; while it holds a query its results replace `children`.
 * `scope` limits it to one conversation.
 */
export function ChatSearch(props: {
  scope?: Conversation;
  selfId: string;
  nowMs: number;
  users: Users;
  autoFocus?: boolean;
  /** A result was picked (it navigates to the message itself). */
  onPick?: () => void;
  /** Escape in an empty box. */
  onClose?: () => void;
  children?: ReactNode;
}) {
  const [query, setQuery] = useState("");
  const label = props.scope ? `Search in ${conversationTitle(props.scope)}` : "Search messages";
  return (
    <>
      <div className="chat-search">
        <input
          type="search"
          value={query}
          maxLength={SEARCH_MAX}
          placeholder={label}
          aria-label={label}
          autoFocus={props.autoFocus}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Escape") return;
            if (query) setQuery("");
            else props.onClose?.();
          }}
        />
      </div>
      {query.trim() ? (
        <SearchResults
          query={query}
          scope={props.scope}
          selfId={props.selfId}
          nowMs={props.nowMs}
          users={props.users}
          onPick={() => {
            if (!props.scope) return props.onPick?.();
            setQuery("");
            props.onPick?.();
          }}
        />
      ) : (
        props.children
      )}
    </>
  );
}

function SearchResults(props: {
  query: string;
  scope: Conversation | undefined;
  selfId: string;
  nowMs: number;
  users: Users;
  onPick: () => void;
}) {
  const { scope, selfId, nowMs } = props;
  const text = useDebounced(searchText(props.query), SEARCH_DEBOUNCE_MS);
  const results = useInfiniteQuery({
    queryKey: ["chat-search", scope?.id ?? null, text],
    enabled: text !== null,
    staleTime: 30_000,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page: SearchPage) => page.next_page_token ?? undefined,
    queryFn: async ({ pageParam }): Promise<SearchPage> =>
      unwrap(
        await api.GET("/me/messages/search", {
          params: {
            query: {
              q: text!,
              ...(scope ? { conversation_id: scope.id } : {}),
              ...(pageParam ? { page_token: pageParam } : {}),
            },
          },
        }),
      ),
  });
  const pages = useMemo(() => results.data?.pages ?? [], [results.data]);
  const users = useMemo(
    () => pages.reduce((all, page) => withUsers(all, page.users), props.users),
    [pages, props.users],
  );
  const conversations = useMemo(
    () => new Map(pages.flatMap((page) => page.conversations).map((c) => [c.id, c])),
    [pages],
  );
  // Results that disappeared since the search go from the list too.
  const items = pages.flatMap((page) => page.items).filter((message) => !isExpired(message, nowMs));
  const words = searchWords(text ?? "");

  let status: string | null = null;
  if (searchText(props.query) === null) status = "Type a word to search for.";
  else if (results.isError) status = errorMessage(results.error);
  else if (text === null || results.isPending) status = "Searching…";
  else if (items.length === 0) status = "No messages found.";
  return (
    <div className="chat-search-results" aria-live="polite" aria-busy={results.isFetching}>
      {status && <p className={`chat-inbox-note ${results.isError ? "error" : "muted"}`}>{status}</p>}
      <ul className="chat-inbox-list">
        {items.map((message) => {
          const conversation = conversations.get(message.conversation_id);
          const title = conversation ? conversationTitle(conversation) : "Chat";
          const who = message.sender_id === selfId ? "You" : personName(users, message.sender_id);
          return (
            <li key={message.id}>
              <Link
                to={`/chats/${message.conversation_id}?seq=${message.seq}`}
                className="chat-inbox-item chat-search-item"
                onClick={props.onPick}
              >
                {!scope && conversation && (
                  <MemberAvatar
                    userId={conversation.peer_id ?? conversation.circle_id ?? conversation.id}
                    name={title}
                    avatarUrl={conversation.avatar_small_url ?? null}
                    isSelf={false}
                  />
                )}
                <span className="chat-inbox-text">
                  <span className="chat-inbox-top">
                    <span className="chat-inbox-title">{scope ? who : title}</span>
                    <time className="chat-inbox-time" dateTime={message.created_at}>
                      {inboxTime(message.created_at, nowMs)}
                    </time>
                  </span>
                  <span className="chat-search-snippet">
                    {!scope && conversation?.kind !== "direct" && `${who}: `}
                    {!scope && conversation?.kind === "direct" && message.sender_id === selfId && "You: "}
                    {snippet(message.body ?? "", words).map((segment, index) =>
                      segment.match ? <mark key={index}>{segment.text}</mark> : segment.text,
                    )}
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      {results.hasNextPage && (
        <p className="chat-inbox-note">
          <button
            type="button"
            className="link-button"
            disabled={results.isFetchingNextPage}
            onClick={() => void results.fetchNextPage()}
          >
            {results.isFetchingNextPage ? "Loading…" : "More results"}
          </button>
        </p>
      )}
    </div>
  );
}
